// src/components/profile/PersonalInfoForm.jsx
import React, { useState } from 'react';
import { Save, Mail, Phone, User, Building, Calendar } from 'lucide-react';

const PersonalInfoForm = ({ initialData, onUpdate }) => {
  const [formData, setFormData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      onUpdate(formData);
      setIsEditing(false);
      setIsSaving(false);
      alert('Profile updated successfully!');
    }, 1000);
  };

  const handleCancel = () => {
    setFormData(initialData);
    setIsEditing(false);
  };

  const fields = [
    { 
      id: 'name', 
      label: 'Full Name', 
      icon: User, 
      type: 'text', 
      required: true,
      placeholder: 'Enter your full name'
    },
    { 
      id: 'email', 
      label: 'Email Address', 
      icon: Mail, 
      type: 'email', 
      required: true,
      placeholder: 'Enter your email'
    },
    { 
      id: 'phone', 
      label: 'Phone Number', 
      icon: Phone, 
      type: 'tel', 
      required: false,
      placeholder: '+1 (555) 123-4567'
    },
    { 
      id: 'department', 
      label: 'Department', 
      icon: Building, 
      type: 'text', 
      required: true,
      placeholder: 'Enter department'
    },
    { 
      id: 'position', 
      label: 'Position', 
      icon: User, 
      type: 'text', 
      required: true,
      placeholder: 'Enter position'
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
          <p className="text-sm text-gray-500">Update your personal details here</p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Edit Profile
          </button>
        ) : (
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {fields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="relative">
                <field.icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type={field.type}
                  name={field.id}
                  value={formData[field.id]}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    isEditing 
                      ? 'border-gray-300 bg-white' 
                      : 'border-gray-200 bg-gray-50 text-gray-500'
                  }`}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Read-only fields */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-500 mb-4">Additional Information (Read Only)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Employee ID</label>
              <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="font-mono text-gray-900">{formData.employeeId}</span>
              </div>
            </div>
            <div>
              <label className="flex items-center text-sm font-medium text-gray-500">
                <Calendar className="h-4 w-4 mr-2" />
                Join Date
              </label>
              <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-900">{formData.joinDate}</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoForm;